import { writable } from "svelte/store";
import { answered, stores } from "./loader";

// One store per view name, filled by load and handed out by fetch.
const views = stores(
  (def) => def.d && def.d.v,
  () => /** @type {object} */ ({}),
);

// The site's facts, as its phone, email, address and hours, kept once in the view site and shown wherever ${site.name} stands.
const siteView = "site";
const factMark = "${site.";

/** @type {Record<string, unknown>} */
let _facts = {};
const _factsStore = writable(_facts);

/**
 * Keeps the site's facts and tells every part drawing them.
 * @param {Record<string, unknown>} facts
 */
function keepFacts(facts) {
  _facts = facts;
  _factsStore.set(facts);
}

/** @type {Promise<void> | undefined} */
let _factsAsked;

/**
 * Fetch the data for a component
 * @param {string} name - view name
 * @returns {Promise<object>}
 */
function fetchData(name) {
  return answered(
    fetch(`/api/data`, {
      method: `POST`,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ view: name }),
    }),
  );
}

const content = {
  /**
   * Fetches a store which will return
   * the content
   * @param {string} name - rows of data
   * @returns {Promise<import("svelte/store").Writable<object>>}
   */
  fetch: views.fetch,
  /**
   * Loads content into stores for all the
   * view names provided
   * @param {string[]} viewNames
   */
  load: async (viewNames) => {
    if (viewNames && Array.isArray(viewNames) && viewNames.length > 0) {
      viewNames.forEach((name) => {
        fetchData(name).then((data) => {
          views.keep(name, data);
          // A row may show a fact of the site, in its words or its rich text, where the page shows none.
          content.loadFacts(data);
        });
      });
    }
  },
  /**
   * Loads the rows of every view a page's or widget's definitions draw from
   * @param {unknown} defs - definitions about to be drawn
   */
  loadIn: async (defs) => {
    await content.load(views.names(defs));
  },
  /**
   * Forgets the facts of the page shown before, as a page opened anew.
   */
  forgetFacts: () => {
    keepFacts({});
    _factsAsked = undefined;
  },
  /**
   * Has the site's facts when defs show one, asking once a page; resolves when they are in, or the server holds none.
   * @param {unknown} defs - definitions about to be drawn
   */
  loadFacts: async (defs) => {
    if (!JSON.stringify(defs ?? null).includes(factMark)) {
      return;
    }
    _factsAsked ??= fetchData(siteView).then((data) => {
      keepFacts(
        data && typeof data === "object" && !Array.isArray(data)
          ? /** @type {Record<string, unknown>} */ (data)
          : {},
      );
    });
    await _factsAsked;
  },
  /**
   * The site's facts as loaded, by name.
   * @returns {Record<string, unknown>}
   */
  facts: () => _facts,
  /**
   * Calls told with the site's facts now and each time they are kept anew; answers the call that stops it.
   * @param {(facts: Record<string, unknown>) => void} told
   * @returns {() => void}
   */
  watchFacts: (told) => _factsStore.subscribe(told),
};
export default content;
